import { Box, Card, CardContent, Typography, Grid, Autocomplete, TextField, Button, Switch } from "@mui/material";
import React, {useState, useEffect} from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from "react-router-dom";
import Snack from "components/Snack";
import Skeleton from "./skeleton"; 
import _ from "lodash";
import { updateImWorkforceAccess } from "redux/actions/addEditWorker";
import { useRemoteConfig } from "providers";
import { convertStringToArray } from "utils/rcUtils";
import { getLocations } from "../utils";

const IM_WORKFORCE_ROLES = ["Facility Supervisor", "Operation Lead", "Admin"];

const ImWorkforce = ({handleChange}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const workerData = location.state && location.state.worker ? location.state.worker : null;
  const user = useSelector((state) => state.user.auth.user);
  const imWorkforceData = useSelector(state => state.addEditWorker.workerRoles.imWorkforce || {});
  const loadingRoles = useSelector(state => state.addEditWorker.loadingRoles);
  const imtRole = useSelector((state) => state.user.imtRole || null);
  const imWorkforceRolesConfig = useRemoteConfig("imWorkforceRoles");
  const roleOptions = imWorkforceRolesConfig
    ? convertStringToArray(imWorkforceRolesConfig)
    : [];
  const imWorkforceRoleOptions = roleOptions.length > 0 ? roleOptions : IM_WORKFORCE_ROLES;
  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [snackControl, setSnackControl] = useState({ open: false, message: "", severity: 'success' });
  const [disableSave, setDisableSave] = useState(true);
  const [readOnly, setReadOnly] = useState(true);
  const [buildingOptions, setBuildingOptions] = useState([]);

  useEffect(() => {
    if(imtRole?.userRole?.role !== "Support"){
          setReadOnly(false);
    }
  },[imtRole])

  useEffect(() => {
    if (buildingOptions.length === 0) {
      getLocations(workerData).then((data) => {
        const { activityEnabledList = [] } = data || {};
        const options = [];
        activityEnabledList.forEach((record) => {
          const allBuildings = record?.buildings || {};
          Object.keys(allBuildings).forEach((building) => {
            if (allBuildings[building]?.activityEnabled === true) {
              options.push({
                buildingID: building,
                districtID: record?.district || "",
              });
            }
          });
        });
        setBuildingOptions(options);
      });
    }
  }, []);

  const normalizeRole = (role) => {
    if (Array.isArray(role)) {
      return role;
    }
    if (typeof role === "string" && role.length > 0) {
      return [role];
    }
    return [];
  };

  useEffect(() => {
    if(imWorkforceData && Object.keys(imWorkforceData).length>0){
      const data = _.cloneDeep(imWorkforceData || {});
      data.role = normalizeRole(data.role);
      data.buildings = Array.isArray(data.buildings) ? data.buildings : [];
      setFormValues(data);
    }
  }, [imWorkforceData]);

  useEffect(() => {
    validForm(formValues);
  } ,[formValues])

  const validForm = (formValues) => {
    let errors = {};
    if(formValues?.isActive){
      if(!Array.isArray(formValues?.role) || formValues.role.length === 0){
        errors['role'] = "Field is required";
      }
      if(!Array.isArray(formValues?.buildings) || formValues.buildings.length === 0){
        errors['buildings'] = "Field is required";
      }
    }
    setFormErrors(errors);
    const isValid = Object.keys(errors).length === 0 && !_.isEqual(formValues, {
      ...imWorkforceData,
      role: normalizeRole(imWorkforceData?.role),
      buildings: Array.isArray(imWorkforceData?.buildings) ? imWorkforceData.buildings : []
    });
    setDisableSave(!isValid);
    return isValid;
  }

  const handleCloseSnack =  () => {
    setSnackControl(state => ({...state, open:false, message: ""}));
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try{
      const isValidForm = validForm(formValues);
      if(!isValidForm){
        setSnackControl({
          open: true,
          message: "Invalid data to submit. Please check!",
          severity: "info"
        })
        return;
      }

      const data = {
        actionTS: new Date().toISOString(),
        actionBy: user?.workerId,
        role: normalizeRole(formValues?.role),
        buildings: formValues?.buildings || [],
        activityFamilyId: 'imWorkforce',
        isActive: formValues?.isActive ? true : false
      }
      const response = await dispatch(updateImWorkforceAccess(data, workerData));
      if(response && response.status){
        setSnackControl({
          open: true,
          message: "IM Workforce Access updated successfully.",
          severity: 'success'
        })
      }else{
        setSnackControl({
          open: true,
          message: "IM Workforce Access update failed.",
          severity: 'error'
        })
      }

    }catch(error){
      console.log("ImWorkforce handleSubmit error", error);
      setSnackControl({
        open: true,
        message: "IM Workforce Access failed to submit.",
        severity: "error"
      })
    }
  }


  return (
    <>
      <Box>
        <form action="" id="formData">
           <Snack 
              openSnack={snackControl?.open} 
              severity={snackControl?.severity} 
              message={snackControl?.message} 
              handleCloseSnack={handleCloseSnack}>
            </Snack>
          
          <Card sx={{ overflow: "auto", width: "100%", height:'calc(100vh - 160px)'}}>
          {loadingRoles ? <Skeleton /> : (
            <CardContent
              style={{ marginLeft: "0%", border: "none", paddingTop: 40 }}
            >
              <Typography variant="h5" color="#616161" marginBottom={3}>
                IM Workforce
              </Typography>
              <hr id="customHr2" />
              <>
                <Grid container rowSpacing={3} marginTop={2} paddingLeft={3}>
                  <Grid
                    container
                    columnSpacing={3}
                    
                    marginBottom={2}
                    alignItems="center"
                  >
                    <Grid item xs={2}  md={2} lg ={0.7}>
                      <Typography sx={{ fontSize: "18px" }}>Enable</Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Switch 
                        inputProps={{ 'aria-label': 'Enable IM Workforce Role switch' }}
                        checked={formValues.isActive}
                        onChange={(e) => {
                          let checked = e.target.checked;
                          setFormValues({ ...formValues, isActive: checked });
                        }}
                        disabled={readOnly}
                      />
                    </Grid>
                  </Grid>
                  <Grid container columnSpacing={5} spacing={3} marginTop={3}>
                    <Grid item xs={12} sm={12} md={6} lg={3}>
                      <Autocomplete
                        renderInput={(params) => (
                          <TextField {...params} 
                          label="Select Role" 
                          required
                          error={formErrors?.role}
                          helperText={formErrors?.role}
                          />
                        )}
                        id="role"
                        name="role"
                        size="small"
                        multiple
                        disableCloseOnSelect
                        options={imWorkforceRoleOptions}
                        value={normalizeRole(formValues?.role)}
                        onChange={(e, v)=>{
                          setFormValues(prevState => ({...prevState, role: v ? v : []}))
                        }}
                        disabled={!formValues?.isActive || readOnly}
                        readOnly={readOnly}
                      />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} lg={3}>
                      <Autocomplete
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Building"
                            required
                            error={formErrors?.buildings}
                            helperText={formErrors?.buildings}
                          />
                        )}
                        id="buildings"
                        name="buildings"
                        size="small"
                        multiple
                        disableCloseOnSelect
                        options={buildingOptions || []}
                        getOptionLabel={(option) => option.buildingID || ""}
                        isOptionEqualToValue={(option, value) =>
                          value?.buildingID && value?.buildingID === option.buildingID
                        }
                        value={formValues?.buildings || []}
                        onChange={(e, v) => {
                          setFormValues((prevState) => ({
                            ...prevState,
                            buildings: v ? v : [],
                          }));
                        }}
                        disabled={!formValues?.isActive || readOnly}
                        readOnly={readOnly}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={5}
                  style={{ marginTop: "2%", textAlign: "right", marginLeft: 4 }}
                >
                  <div>
                    <Button
                      size="medium"
                      variant="outlined"
                      onClick={(e) => handleChange(e, 0)}
                      style={{ marginRight: 10, display: readOnly ? "none" : "" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      style={{ marginRight: 30, display: readOnly ? "none" : "" }}
                      size="medium"
                      variant="contained"
                      id="svBtn"
                      disabled={disableSave}
                      onClick={handleSubmit}
                    >
                      Save
                    </Button>
                  </div>
                </Grid>
              </>
            </CardContent>
          )}
          </Card>
        </form>
      </Box>
    </>
  );
};

export default ImWorkforce;
